import * as React from 'react';
import { Inbox, type LucideIcon } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface EmptyStateProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  /** Headline — the most important thing to convey ("No orders yet"). */
  title: React.ReactNode;
  /** Optional one-line explainer. */
  description?: React.ReactNode;
  /** Icon. Defaults to Inbox; pass `null` to render no icon. */
  icon?: LucideIcon | null;
  /** Primary action (typically a <Button />). */
  action?: React.ReactNode;
  /** Compact variant — smaller padding and icon for inline / card use. */
  compact?: boolean;
}

/**
 * EmptyState — the canonical "nothing here yet" placeholder.
 *
 * Used inside tables, lists, cards, and dashboards. The default icon is
 * `Inbox`; override with any Lucide icon (or `null` to drop it entirely).
 */
export const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(
  (
    {
      title,
      description,
      icon = Inbox,
      action,
      compact,
      className,
      ...props
    },
    ref,
  ) => {
    const Icon = icon;
    return (
      <div
        ref={ref}
        className={cn(
          'flex flex-col items-center justify-center text-center',
          compact ? 'gap-2 px-4 py-8' : 'gap-3 px-6 py-14',
          className,
        )}
        {...props}
      >
        {Icon && (
          <span
            className={cn(
              'inline-flex items-center justify-center rounded-full bg-muted text-muted-foreground',
              compact ? 'h-9 w-9' : 'h-12 w-12',
            )}
          >
            <Icon className={compact ? 'h-4 w-4' : 'h-5 w-5'} />
          </span>
        )}
        <div className="space-y-1">
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
          {description && (
            <p className="mx-auto max-w-sm text-xs leading-relaxed text-muted-foreground">
              {description}
            </p>
          )}
        </div>
        {action && <div className="mt-1">{action}</div>}
      </div>
    );
  },
);
EmptyState.displayName = 'EmptyState';
